// Regras puras do Flowbuilder Aeros (paleta, meta visual por tipo de nó,
// validação do fluxo, conflito de frases gatilho, templates de fluxo novo,
// "salvo há X"). Sem React — testado em __tests__/flowRules.test.js.
import { i18n } from "../../translate/i18n";

// Tons do mockup (cor do ícone, fundo do tile).
export const TONES = Object.freeze({
  msg: { color: "#22D3EE", bg: "rgba(34,211,238,0.12)", stateKey: "info" },
  logic: { color: "#C08BFF", bg: "rgba(160,68,255,0.15)", stateKey: "purple" },
  act: { color: "#34D399", bg: "rgba(52,211,153,0.12)", stateKey: "success" },
  ai: { color: "#FBBF24", bg: "rgba(251,191,36,0.12)", stateKey: "warning" },
  red: { color: "#F87171", bg: "rgba(248,113,113,0.12)", stateKey: "error" },
});

// type do react-flow → { tone, icon (nome Material), palette (chave usada em
// clickActions do editor; null = não entra na paleta), labelKey }.
// Os ícones são resolvidos em nodes/AerosNode.js (mapa nome → componente).
export const NODE_META = Object.freeze({
  start: { tone: "act", icon: "play", labelKey: "aeros.flow.nodes.start", palette: null, terminal: false },
  message: { tone: "msg", icon: "chat", labelKey: "aeros.flow.nodes.text", palette: "text" },
  menu: { tone: "msg", icon: "menu", labelKey: "aeros.flow.nodes.menu", palette: "menu" },
  singleBlock: { tone: "msg", icon: "layers", labelKey: "aeros.flow.nodes.content", palette: "content" },
  img: { tone: "msg", icon: "image", labelKey: "aeros.flow.nodes.image", palette: "img" },
  audio: { tone: "msg", icon: "mic", labelKey: "aeros.flow.nodes.audio", palette: "audio" },
  video: { tone: "msg", icon: "video", labelKey: "aeros.flow.nodes.video", palette: "video" },
  conditionCompare: { tone: "logic", icon: "altRoute", labelKey: "aeros.flow.nodes.condition", palette: "condition" },
  randomizer: { tone: "logic", icon: "shuffle", labelKey: "aeros.flow.nodes.randomizer", palette: "random" },
  interval: { tone: "logic", icon: "timer", labelKey: "aeros.flow.nodes.interval", palette: "interval" },
  input: { tone: "logic", icon: "keyboard", labelKey: "aeros.flow.nodes.input", palette: "input" },
  variable: { tone: "logic", icon: "dataObject", labelKey: "aeros.flow.nodes.variable", palette: "variable" },
  switchFlow: { tone: "logic", icon: "swap", labelKey: "aeros.flow.nodes.switchFlow", palette: "switchFlow", terminal: true },
  ticket: { tone: "act", icon: "ticket", labelKey: "aeros.flow.nodes.ticket", palette: "ticket", terminal: true },
  attendant: { tone: "act", icon: "agent", labelKey: "aeros.flow.nodes.attendant", palette: "attendant", terminal: true },
  tag: { tone: "act", icon: "sell", labelKey: "aeros.flow.nodes.tag", palette: "tag" },
  removeTag: { tone: "act", icon: "labelOff", labelKey: "aeros.flow.nodes.removeTag", palette: "removeTag" },
  httpRequest: { tone: "act", icon: "http", labelKey: "aeros.flow.nodes.http", palette: "httpRequest" },
  openai: { tone: "ai", icon: "openai", labelKey: "aeros.flow.nodes.openai", palette: "openai", terminal: true },
  gemini: { tone: "ai", icon: "gemini", labelKey: "aeros.flow.nodes.gemini", palette: "gemini", terminal: true },
});

// Paleta do editor (mockup): grupo → tipos de nó, na ordem do desenho.
export const PALETTE = Object.freeze([
  { key: "messages", labelKey: "aeros.flow.palette.messages", types: ["message", "menu", "singleBlock", "img", "audio", "video"] },
  { key: "logic", labelKey: "aeros.flow.palette.logic", types: ["conditionCompare", "randomizer", "interval", "input", "variable", "switchFlow"] },
  { key: "actions", labelKey: "aeros.flow.palette.actions", types: ["ticket", "attendant", "tag", "removeTag", "httpRequest"] },
  { key: "ai", labelKey: "aeros.flow.palette.ai", types: ["openai", "gemini"] },
]);

const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

// Filtra a paleta pela busca (acento/caixa ignorados). Grupos vazios somem.
export const filterPalette = (query) => {
  const q = norm(query);
  return PALETTE.map((g) => ({
    ...g,
    types: g.types.filter((t) => !q || norm(i18n.t(NODE_META[t].labelKey)).includes(q)),
  })).filter((g) => g.types.length > 0);
};

export const nodeTitle = (node) => i18n.t(NODE_META[node?.type]?.labelKey || "aeros.flow.nodes.unknown");

const trunc = (s, n = 90) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || "");

// Resumo em uma linha do conteúdo do nó (corpo do card e painel lateral).
export const nodeSummary = (node) => {
  const d = node?.data || {};
  switch (node?.type) {
    case "start":
      return i18n.t("aeros.flow.summary.start");
    case "message":
      return trunc(d.label);
    case "menu":
      return trunc(d.message);
    case "singleBlock":
      return i18n.t("aeros.flow.summary.items", { count: (d.seq || []).length });
    case "img":
    case "video":
      return d.url ? String(d.url).split("/").pop() : "";
    case "audio":
      return `${d.url ? String(d.url).split("/").pop() : ""}${d.record ? ` · ${i18n.t("aeros.flow.summary.recorded")}` : ""}`;
    case "conditionCompare":
      return `${d.leftValue || "?"} ${operatorSymbol(d.operator)} ${d.rightValue || ""}`.trim();
    case "randomizer":
      return `${d.percent ?? 50}% / ${100 - (d.percent ?? 50)}%`;
    case "interval":
      return i18n.t("aeros.flow.summary.interval", { sec: d.sec ?? 0 });
    case "input":
      return `${trunc(d.question, 60)}${d.variableName ? ` → {${d.variableName}}` : ""}`;
    case "variable":
      return d.variableName ? `{${d.variableName}} = ${trunc(d.variableValue, 40)}` : "";
    case "switchFlow":
      return d.flowSelected?.name || "";
    case "ticket":
      return d.queue?.name || "";
    case "attendant":
      return d.user?.name || "";
    case "tag":
    case "removeTag":
      return d.tag?.name || "";
    case "httpRequest":
      return `${d.method || "GET"} ${trunc(d.url, 60)}`.trim();
    case "openai":
    case "gemini":
      return d.typebotIntegration?.name || "";
    default:
      return "";
  }
};

// Operadores do nó Condição — os valores são o contrato do backend
// (ActionsWebhookService.compareValues).
export const CONDITION_OPERATORS = Object.freeze([
  { value: "equals", symbol: "==" },
  { value: "notEquals", symbol: "!=" },
  { value: "greaterOrEqual", symbol: ">=" },
  { value: "lessOrEqual", symbol: "<=" },
  { value: "greaterThan", symbol: ">" },
  { value: "lessThan", symbol: "<" },
  { value: "contains", symbol: "contains", labelKey: "aeros.flow.condition.ops.contains" },
  { value: "startsWith", symbol: "startsWith", labelKey: "aeros.flow.condition.ops.startsWith" },
  { value: "endsWith", symbol: "endsWith", labelKey: "aeros.flow.condition.ops.endsWith" },
  { value: "isEmpty", symbol: "isEmpty", labelKey: "aeros.flow.condition.ops.isEmpty", unary: true },
  { value: "isNotEmpty", symbol: "isNotEmpty", labelKey: "aeros.flow.condition.ops.isNotEmpty", unary: true },
]);

export const operatorSymbol = (value) => {
  const op = CONDITION_OPERATORS.find((o) => o.value === value);
  if (!op) return "==";
  return op.labelKey ? i18n.t(op.labelKey) : op.symbol;
};

// Saídas (source handles) que cada tipo de nó precisa ter conectadas.
const outputsOf = (node) => {
  switch (node.type) {
    case "conditionCompare":
      return [
        { handle: "true", labelKey: "aeros.flow.condition.true" },
        { handle: "false", labelKey: "aeros.flow.condition.false" },
      ];
    case "randomizer":
      return [{ handle: "a" }, { handle: "b" }];
    // alças com id próprio (contrato dos nós inline)
    case "variable":
      return [{ handle: "variable-out" }];
    case "httpRequest":
      return [{ handle: "httpRequest-out" }];
    case "menu":
      return (node.data?.arrayOption || []).map((o) => ({
        handle: `a${o.number}`,
        label: `[${o.number}] ${o.value || ""}`,
      }));
    default:
      return NODE_META[node.type]?.terminal ? [] : [{ handle: "a" }];
  }
};

// Problemas do fluxo: sem nó de início, nó inalcançável, saída sem conexão.
// Devolve [{ nodeId, kind: "noStart"|"unreachable"|"dangling", handle, label }].
export const findFlowProblems = (nodes = [], edges = []) => {
  const problems = [];
  const start = nodes.find((n) => n.type === "start");
  if (!start) problems.push({ nodeId: null, kind: "noStart" });
  const hasIncoming = new Set(edges.map((e) => e.target));
  nodes.forEach((n) => {
    if (n.type !== "start" && !hasIncoming.has(n.id)) {
      problems.push({ nodeId: n.id, kind: "unreachable" });
    }
    outputsOf(n).forEach((out) => {
      const connected = edges.some(
        (e) => e.source === n.id && (e.sourceHandle || "a") === out.handle
      );
      if (!connected) {
        problems.push({
          nodeId: n.id,
          kind: "dangling",
          handle: out.handle,
          label: out.labelKey ? i18n.t(out.labelKey) : out.label,
        });
      }
    });
  });
  return problems;
};

export const problemText = (p, nodes = []) => {
  const node = nodes.find((n) => n.id === p.nodeId);
  const name = node ? nodeTitle(node) : "";
  if (p.kind === "noStart") return i18n.t("aeros.flow.problems.noStart");
  if (p.kind === "unreachable") return i18n.t("aeros.flow.problems.unreachable", { node: name });
  return p.label
    ? i18n.t("aeros.flow.problems.danglingLabel", { node: name, label: p.label })
    : i18n.t("aeros.flow.problems.dangling", { node: name });
};

// Conflito de frases gatilho: outra campanha ativa, na mesma conexão, com a
// mesma frase (normalizada). Devolve [{ text, campaignName, whatsappId }].
export const phraseConflicts = (phrases, whatsappIds, campaigns, currentId) => {
  const out = [];
  const mine = (phrases || []).map((p) => norm(p.text)).filter(Boolean);
  (campaigns || []).forEach((c) => {
    if (!c || c.id === currentId || c.status === false) return;
    const shared = (c.whatsappIds || []).filter((w) => (whatsappIds || []).includes(w));
    if (!shared.length) return;
    (c.phrase || []).forEach((p) => {
      const t = norm(p?.text ?? p);
      if (t && mine.includes(t) && !out.some((o) => o.text === t && o.campaignName === c.name)) {
        out.push({ text: t, campaignName: c.name, whatsappId: shared[0] });
      }
    });
  });
  return out;
};

// Busca da lista de frases: nome, frase ou nome do fluxo.
export const matchesPhraseSearch = (row, flowName, query) => {
  const q = norm(query);
  if (!q) return true;
  return (
    norm(row.name).includes(q) ||
    norm(flowName).includes(q) ||
    (row.phrase || []).some((p) => norm(p?.text ?? p).includes(q))
  );
};

// "Salvo há X" — devolve { key, count } para i18n.
export const savedAgo = (date, now = Date.now()) => {
  if (!date) return { key: "aeros.flow.saved.never" };
  const diff = Math.max(0, now - new Date(date).getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return { key: "aeros.flow.saved.now" };
  if (min < 60) return { key: "aeros.flow.saved.minutes", count: min };
  const h = Math.floor(min / 60);
  if (h < 24) return { key: "aeros.flow.saved.hours", count: h };
  return { key: "aeros.flow.saved.days", count: Math.floor(h / 24) };
};

// Templates do modal "Novo fluxo" — nós no formato salvo pelo editor.
const rid = () => Math.random().toString(36).slice(2, 12) + Math.random().toString(36).slice(2, 12);
const node = (id, type, x, y, data) => ({ id, type, position: { x, y }, data });
const edge = (source, target, sourceHandle = "a") => ({
  id: `e-${source}-${sourceHandle}-${target}`,
  source,
  target,
  sourceHandle,
  type: "buttonedge",
});

export const FLOW_TEMPLATES = Object.freeze([
  {
    id: "blank",
    icon: "add",
    tone: "msg",
    titleKey: "aeros.flow.templates.blank",
    subKey: "aeros.flow.templates.blankSub",
    build: () => ({ nodes: [node("1", "start", 250, 100, { label: "Inicio do fluxo" })], connections: [] }),
  },
  {
    id: "welcome",
    icon: "wave",
    tone: "act",
    titleKey: "aeros.flow.templates.welcome",
    subKey: "aeros.flow.templates.welcomeSub",
    build: () => {
      const m = rid();
      const menu = rid();
      return {
        nodes: [
          node("1", "start", 40, 100, { label: "Inicio do fluxo" }),
          node(m, "message", 280, 100, { label: i18n.t("aeros.flow.templates.welcomeMessage") }),
          node(menu, "menu", 560, 80, {
            message: i18n.t("aeros.flow.templates.menuMessage"),
            typeMenu: "number",
            arrayOption: [1, 2, 3].map((n) => ({ number: n, value: i18n.t(`aeros.flow.templates.menuOption${n}`) })),
          }),
        ],
        connections: [edge("1", m), edge(m, menu)],
      };
    },
  },
  {
    id: "triage",
    icon: "agent",
    tone: "logic",
    titleKey: "aeros.flow.templates.triage",
    subKey: "aeros.flow.templates.triageSub",
    build: () => {
      const menu = rid();
      return {
        nodes: [
          node("1", "start", 40, 100, { label: "Inicio do fluxo" }),
          node(menu, "menu", 300, 80, {
            message: i18n.t("aeros.flow.templates.menuMessage"),
            typeMenu: "number",
            arrayOption: [1, 2, 3].map((n) => ({ number: n, value: i18n.t(`aeros.flow.templates.menuOption${n}`) })),
          }),
        ],
        connections: [edge("1", menu)],
      };
    },
  },
  {
    id: "nps",
    icon: "star",
    tone: "ai",
    titleKey: "aeros.flow.templates.nps",
    subKey: "aeros.flow.templates.npsSub",
    build: () => {
      const q = rid();
      const c = rid();
      const thanks = rid();
      return {
        nodes: [
          node("1", "start", 40, 100, { label: "Inicio do fluxo" }),
          node(q, "input", 280, 80, { question: i18n.t("aeros.flow.templates.npsQuestion"), variableName: "nota" }),
          node(c, "input", 580, 80, { question: i18n.t("aeros.flow.templates.npsComment"), variableName: "comentario" }),
          node(thanks, "message", 880, 100, { label: i18n.t("aeros.flow.templates.npsThanks") }),
        ],
        connections: [edge("1", q), edge(q, c), edge(c, thanks)],
      };
    },
  },
]);
